import { memo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BoltIcon, ArrowPathIcon, MapPinIcon, CheckCircleIcon, SignalIcon, StarIcon } from '@heroicons/react/24/outline'
import { useLiveFeed } from '../../hooks/useLiveFeed'
import { createLogger } from '../../utils/logger'
import { trackButtonClick } from '../../utils/analytics'

const logger = createLogger('LiveFeedPanel')

/**
 * Formats a date as a relative time string (e.g. "2m ago", "1h ago", "3d ago")
 */
function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

/**
 * LiveFeedPanel - Global sighting feed, newest first, load-more pagination
 * @atomic @idempotent
 */
const LiveFeedPanel = memo(function LiveFeedPanel({ userLocation = null }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { items, loading, loadingMore, error, hasMore, loadMore } = useLiveFeed(userLocation)

  const handleLoadMore = useCallback(() => {
    logger.info('Load more tapped', { currentCount: items.length })
    trackButtonClick('live-feed-load-more', 'LiveFeedPanel', { currentCount: items.length })
    loadMore()
  }, [loadMore, items.length])

  const handleView = useCallback((entityId) => {
    logger.info('Navigate to entity from live feed', { entityId })
    navigate(`/entity/${entityId}`)
  }, [navigate])

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      {loading && items.length === 0 && (
        <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
          {t('common.loading', 'Loading...')}
        </div>
      )}

      {error && items.length === 0 && (
        <div className="flex items-center justify-center py-12 text-red-500 text-sm px-4 text-center">
          {t('live.error', 'Could not load live feed. Tap refresh to try again.')}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-400 text-sm px-4 text-center">
          <BoltIcon className="w-8 h-8 opacity-30" />
          <p>{t('live.noActivity', 'No sightings yet.')}</p>
          <p className="text-xs">{t('live.noActivityHint', 'Be the first to spot something!')}</p>
        </div>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {items.map((item) => (
            <li key={item.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-start gap-3">
                {/* Entity thumbnail */}
                <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-gray-100">
                  {item.entityImageUrl ? (
                    <img
                      src={item.entityImageUrl}
                      alt={item.entityName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex' }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ display: item.entityImageUrl ? 'none' : 'flex' }}
                  >
                    {item.type === 'vendor_live'
                      ? <SignalIcon className="w-5 h-5 text-primary opacity-60" />
                      : item.type === 'confirmation'
                        ? <CheckCircleIcon className="w-5 h-5 text-green-400" />
                        : item.type === 'review'
                          ? <StarIcon className="w-5 h-5 text-yellow-400" />
                          : <MapPinIcon className="w-5 h-5 text-gray-300" />}
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {item.entityName}
                      </span>
                      {item.type === 'vendor_live' && (
                        <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                          {t('live.liveNow', 'Live')}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {timeAgo(item.createdAt)}
                    </span>
                  </div>
                  {item.address && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {item.address}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    {item.type === 'vendor_live' ? (
                      <span className="text-xs text-gray-400">{t('live.checkItOut', 'Now open')}</span>
                    ) : item.type === 'confirmation' ? (
                      <span className="text-xs text-green-600">
                        {item.reporterName} {t('live.confirmed', 'confirmed')}
                      </span>
                    ) : item.type === 'review' ? (
                      <span className="text-xs text-yellow-600 flex items-center gap-1">
                        <StarIcon className="w-3 h-3" />
                        {item.confirmationCount}/5 — {item.reporterName} {t('live.reviewed', 'reviewed')}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {t('live.by', 'by')} {item.reporterName}
                        {item.confirmationCount > 0 && (
                          <span className="ml-2 text-green-600 font-medium">
                            +{item.confirmationCount}
                          </span>
                        )}
                        {item.entityStatus && (
                          <span className={`ml-2 font-medium px-1.5 py-0.5 rounded-full text-[10px] ${
                            item.entityStatus === 'open' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {item.entityStatus === 'open'
                              ? t('sightings.status.open', 'Open')
                              : t('sightings.status.closed', 'Closed')}
                          </span>
                        )}
                      </span>
                    )}
                    <button
                      onClick={() => handleView(item.entityId)}
                      onTouchEnd={(e) => { e.preventDefault(); handleView(item.entityId) }}
                      className="text-xs text-primary hover:underline flex-shrink-0"
                    >
                      {t('live.viewOnMap', 'View')}
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Load more button / loading / end of feed */}
      {hasMore && !loadingMore && (
        <div className="flex justify-center py-4">
          <button
            onClick={handleLoadMore}
            onTouchEnd={(e) => { e.preventDefault(); handleLoadMore() }}
            className="text-sm text-primary hover:underline px-4 py-2"
          >
            {t('live.loadMore', 'Load more')}
          </button>
        </div>
      )}

      {loadingMore && (
        <div className="flex items-center justify-center py-4">
          <ArrowPathIcon className="w-4 h-4 text-gray-400 animate-spin" />
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <p className="text-center text-xs text-gray-400 py-4">
          {t('live.endOfFeed', 'All caught up')}
        </p>
      )}
    </div>
  )
})

export default LiveFeedPanel
